import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";

import HistoriqueService from "../../../services/smq/HistoriqueService";

const PAGE_SIZE = 25;
const BUSINESS_FIELDS = [
  ["action_corrective", "Action corrective"],
  ["plan_action", "Plan d’action"],
  ["indicateur_efficacite", "Indicateur d’efficacité"],
  ["avancement", "Avancement"],
  ["date_debut", "Date de début"],
  ["date_fin", "Date de fin"],
  ["responsables", "Responsables"],
  ["ordre", "Ordre"],
  ["status", "Statut"],
];

const ACTION_LABELS = {
  AJOUT_ACTION_CORRECTIVE: "Création",
  MODIFICATION_ACTION_CORRECTIVE: "Modification",
  SUPPRESSION_ACTION_CORRECTIVE: "Suppression",
};

const statusLabel = (value) =>
  ({ confirmed: "Confirmée", pending: "En attente" })[value] || value;

const responsableNames = (snapshot) => {
  const responsables = Array.isArray(snapshot?.responsables)
    ? snapshot.responsables
    : [];
  if (responsables.length) {
    return responsables
      .map((item) => item.responsable_name)
      .filter(Boolean)
      .join(", ");
  }
  return snapshot?.responsable_name || "";
};

const fieldValue = (snapshot, field) => {
  if (!snapshot) return "";
  if (field === "responsables") return responsableNames(snapshot);
  if (field === "avancement" && snapshot[field] !== null && snapshot[field] !== "") {
    return `${snapshot[field]} %`;
  }
  if (field === "status") return statusLabel(snapshot[field]);
  return snapshot[field] ?? "";
};

const normalizeComparison = (value) => String(value ?? "").trim();

const changedFields = (entry) =>
  BUSINESS_FIELDS.map(([field, label]) => ({
    after: fieldValue(entry.nouveau_valeur, field),
    before: fieldValue(entry.ancien_valeur, field),
    field,
    label,
  })).filter(
    ({ after, before }) =>
      normalizeComparison(after) !== normalizeComparison(before)
  );

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("fr-FR", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
};

const DisplayValue = ({ children }) => (
  <Typography
    component="div"
    fontSize={12}
    sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
  >
    {String(children || "").trim() || "—"}
  </Typography>
);

DisplayValue.propTypes = { children: PropTypes.node };
DisplayValue.defaultProps = { children: null };

const CorrectiveActionHistoryDialog = ({ action, onClose, open }) => {
  const [state, setState] = useState({
    error: null,
    loading: false,
    page: 1,
    rows: [],
    totalPages: 0,
  });
  const requestIdRef = useRef(0);

  const loadHistory = useCallback(
    async (page = 1) => {
      if (!action?.plan_action_id) return;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setState((current) => ({
        ...current,
        error: null,
        loading: true,
        page,
      }));
      try {
        const result = await HistoriqueService.getActionHistory(
          action.plan_action_id,
          page,
          PAGE_SIZE
        );
        if (requestId !== requestIdRef.current) return;
        setState({
          error: null,
          loading: false,
          page,
          rows: Array.isArray(result?.rows) ? result.rows : [],
          totalPages: Number(result?.pagination?.total_pages || 0),
        });
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        setState((current) => ({
          ...current,
          error: error.message,
          loading: false,
          rows: [],
        }));
      }
    },
    [action?.plan_action_id]
  );

  useEffect(() => {
    if (open && action?.plan_action_id) loadHistory(1);
    return () => {
      requestIdRef.current += 1;
    };
  }, [action?.plan_action_id, loadHistory, open]);

  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
      <DialogTitle sx={{ alignItems: "center", display: "flex", gap: 1 }}>
        <HistoryOutlinedIcon color="primary" />
        <Box>
          <Typography color="#173d61" fontWeight={800}>
            Historique de l’action corrective
          </Typography>
          <Typography color="text.secondary" fontSize={12}>
            {action?.action_corrective || "Action corrective"}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ backgroundColor: "#f5f8fc" }}>
        {state.loading ? (
          <Stack spacing={1.5}>
            {[1, 2, 3].map((item) => (
              <Skeleton height={120} key={item} variant="rounded" />
            ))}
          </Stack>
        ) : state.error ? (
          <Alert
            action={
              <Button color="inherit" onClick={() => loadHistory(state.page)}>
                Réessayer
              </Button>
            }
            severity="error"
          >
            {state.error}
          </Alert>
        ) : state.rows.length === 0 ? (
          <Typography color="text.secondary" py={5} textAlign="center">
            Aucun historique disponible.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {state.rows.map((entry) => {
              const changes = changedFields(entry);
              return (
                <Paper
                  key={entry.historique_id}
                  sx={{ border: "1px solid #d7e0eb", p: 2 }}
                  variant="outlined"
                >
                  <Box
                    sx={{
                      alignItems: { sm: "center" },
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1,
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography color="#173d61" fontSize={13} fontWeight={800}>
                        {entry.user_name || "Utilisateur non renseigné"}
                      </Typography>
                      <Typography color="text.secondary" fontSize={11}>
                        {formatDateTime(entry.created_at)}
                      </Typography>
                    </Box>
                    <Chip
                      color={
                        entry.action === "SUPPRESSION_ACTION_CORRECTIVE"
                          ? "error"
                          : entry.action === "AJOUT_ACTION_CORRECTIVE"
                            ? "success"
                            : "primary"
                      }
                      label={ACTION_LABELS[entry.action] || entry.action}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  {changes.length ? (
                    <Stack spacing={1.2}>
                      {changes.map((change) => (
                        <Box key={change.field}>
                          <Typography
                            color="#536b91"
                            fontSize={10}
                            fontWeight={800}
                            textTransform="uppercase"
                          >
                            {change.label}
                          </Typography>
                          <Box
                            sx={{
                              display: "grid",
                              gap: 1,
                              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                              mt: 0.5,
                            }}
                          >
                            <Box sx={{ backgroundColor: "#fff4f4", borderRadius: 1, p: 1 }}>
                              <Typography color="text.secondary" fontSize={10}>
                                Avant
                              </Typography>
                              <DisplayValue>{change.before}</DisplayValue>
                            </Box>
                            <Box sx={{ backgroundColor: "#f0faf4", borderRadius: 1, p: 1 }}>
                              <Typography color="text.secondary" fontSize={10}>
                                Après
                              </Typography>
                              <DisplayValue>{change.after}</DisplayValue>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary" fontSize={12}>
                      Aucune différence métier détectée.
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </Stack>
        )}
        {!state.loading && !state.error && state.totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
            <Pagination
              count={state.totalPages}
              onChange={(_, page) => loadHistory(page)}
              page={state.page}
              shape="rounded"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

CorrectiveActionHistoryDialog.propTypes = {
  action: PropTypes.shape({
    action_corrective: PropTypes.string,
    plan_action_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

CorrectiveActionHistoryDialog.defaultProps = { action: null };

export default CorrectiveActionHistoryDialog;
